// src/pages/Contact.jsx
import React, { useState } from "react";
import emailjs from "@emailjs/browser";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [notification, setNotification] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const result = await emailjs.send(
        "service_1yqq5u4",
        "template_aly6r5r",
        formData,
        "Il9uk7E-0Fff7jmsE"
      );

      if (result.status === 200) {
        setNotification("success");
        setNotificationMessage(`Thank you, ${formData.name}! Your message has been sent.`);
        setFormData({ name: "", email: "", message: "" });

        setTimeout(() => setNotification(null), 5000);
      } else {
        setNotification("error");
        setNotificationMessage("Failed to send message. Please try again.");
        setTimeout(() => setNotification(null), 5000);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setNotification("error");
      setNotificationMessage("Network error — please try again later.");
      setTimeout(() => setNotification(null), 5000);
    }
  };

  return (
    <>
      {/* BREADCRUMB SCHEMA */}
      <script type="application/ld+json">
        {`
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://ledotprime.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Contact",
              "item": "https://ledotprime.com/contact"
            }
          ]
        }
        `}
      </script>

      <div className="w-full flex flex-col items-center bg-white text-black pt-20 relative">

        {/* NOTIFICATION */}
        {notification && (
          <div
            className={`fixed top-10 right-10 z-50 px-6 py-4 rounded-xl shadow-2xl text-white font-semibold text-lg transform transition-all duration-500 ${
              notification === "success"
                ? "bg-green-500 animate-slide-in"
                : "bg-red-500 animate-slide-in"
            }`}
          >
            {notificationMessage}
          </div>
        )}

        {/* HERO SECTION */}
        <section className="w-full max-w-6xl py-24 px-6 md:px-0 text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6">
            Get in Touch
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions about our GPS tracking solutions? Reach out and our experts will assist you immediately.
          </p>
        </section>

        {/* FORM + CONTACT INFO */}
        <section className="w-full max-w-6xl px-6 md:px-0 py-12 grid md:grid-cols-2 gap-12 items-start">

          {/* CONTACT FORM */}
          <form
            onSubmit={handleSubmit}
            className="bg-gray-50 rounded-3xl shadow-xl p-10 flex flex-col space-y-6 transition-transform transform hover:-translate-y-1 duration-300"
          >
            <h2 className="text-3xl font-bold mb-4">Send Us a Message</h2>

            {/* GRID: Name + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  className="border border-gray-300 rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  required
                  className="border border-gray-300 rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                />
              </div>
            </div>

            {/* Message */}
            <div className="w-full">
              <textarea 
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                rows="6"
                required
                className="border border-gray-300 rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg resize-none w-full"
              />
            </div>

            <button
              type="submit"
              className="bg-black text-white font-semibold px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors duration-300 text-lg"
            >
              Send Message
            </button>
          </form>

          {/* CONTACT INFO + MAP */}
          <div className="flex flex-col space-y-8">

            {/* Contact Details */}
            <div className="bg-gray-50 rounded-3xl shadow-xl p-8 flex flex-col space-y-4 transition-transform transform hover:-translate-y-1 duration-300">
              <h2 className="text-3xl font-bold">Contact Information</h2>

              <p className="text-gray-600 text-lg">
                📍 Lokko Street, Osu — Total Filling Station, Accra, Ghana
              </p>

              <p className="text-gray-600 text-lg">📞 +233 508 499 531</p>
              <p className="text-gray-600 text-lg">✉️ info@ledotprime.com</p>
              <p className="text-gray-600 text-lg">⏰ Mon - Sat: 8:00 AM - 6:00 PM</p>
            </div>

            {/* MAP */}
            <div className="w-full h-64 rounded-3xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3979.01328478083!2d-0.1808808!3d5.558488899999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfdf9aa3e7b1c4ef%3A0x9e0cd2b2d0bbd10c!2sTotalEnergies%20-%20Osu%20Branch%20(Lokko%20Street)!5e0!3m2!1sen!2sgh!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ledot Prime Location"
              ></iframe>
            </div>

          </div>
        </section>

        {/* TAILWIND ANIMATION */}
        <style>
          {`
            @keyframes slide-in {
              0% { transform: translateX(100%); opacity: 0; }
              100% { transform: translateX(0); opacity: 1; }
            }
            .animate-slide-in {
              animation: slide-in 0.5s ease-out forwards;
            }
          `}
        </style>

      </div>
    </>
  );
}
